import React, { useState } from 'react';
import axios from 'axios';
import { Search, BookOpen, Loader2, Lightbulb, Zap, History } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { VoiceInput } from '../components/VoiceInput';
import { PageHeader } from '../components/PageHeader';
import { ResultCard, RichText } from '../components/AIResultSections';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export const MedicalSearchPage = () => {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const exampleQuestions = [
    "Posologie amoxicilline angine adulte ?",
    "Critères hospitalisation pneumonie ?",
    "Antibiothérapie cystite femme enceinte ?",
    "Posologie paracétamol enfant 15kg ?",
    "Indication TDR angine ?",
    "Protocole ALD diabète type 2 Ameli ?"
  ];

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/medical-search`, { query });
      setResult(response.data.result);
      setSearchHistory(prev => [{ query, result: response.data.result, timestamp: new Date() }, ...prev.slice(0, 4)]);
    } catch (error) {
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceTranscription = (text) => {
    setQuery(text);
    // Auto-submit after voice input
    setTimeout(() => {
      handleSearch();
    }, 300);
  };

  const loadExample = (example) => {
    setQuery(example);
  };

  const loadHistoryItem = (item) => {
    setQuery(item.query);
    setResult(item.result);
  };

  const prettify = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);
  const accentForLabel = (label) => {
    const u = (label || '').toUpperCase();
    if (/ATTENTION|ALERTE|URGENCE|CONTRE-INDIC|DANGER|RISQUE|INTERACTION/.test(u)) return 'amber';
    if (/SOURCE|R[ÉE]F[ÉE]RENCE|BIBLIO/.test(u)) return 'slate';
    if (/R[ÉE]PONSE/.test(u)) return 'blue';
    if (/D[ÉE]TAIL/.test(u)) return 'cyan';
    return 'emerald';
  };

  const parseStructuredResponse = (text) => {
    const out = [];
    let current = null;
    (text || '').split('\n').forEach((line) => {
      const m = line.match(/^\s*\[([^\]]{2,60})\]\s*(.*)$/);
      if (m) {
        if (current) out.push(current);
        current = { title: prettify(m[1]), accent: accentForLabel(m[1]), lines: m[2] ? [m[2]] : [] };
      } else if (line.trim().startsWith('━')) {
        // séparateur décoratif ignoré
      } else if (current) {
        current.lines.push(line);
      } else if (line.trim()) {
        current = { title: null, accent: 'slate', lines: [line] };
      }
    });
    if (current) out.push(current);
    return out
      .map((s) => ({ ...s, content: s.lines.join('\n').trim() }))
      .filter((s) => s.content || s.title);
  };

  const sections = result ? parseStructuredResponse(result) : [];

  const historyList = (
    <div className="space-y-2">
      {searchHistory.length === 0 ? (
        <p data-testid="empty-history" className="text-slate-400 text-sm text-center py-8">
          Aucune recherche récente
        </p>
      ) : searchHistory.map((item, index) => (
        <button
          key={item.timestamp.getTime()}
          data-testid={`history-item-${index}`}
          onClick={() => { loadHistoryItem(item); setShowHistory(false); }}
          className="w-full text-left p-3 min-h-[48px] rounded-xl bg-slate-900/40 border border-slate-800 hover:bg-slate-900/60 active:scale-[0.99] transition-all"
        >
          <p className="text-sm text-slate-300 line-clamp-2">{item.query}</p>
          <p className="text-xs text-slate-500 mt-1">
            {item.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="medical-search-page" className="flex flex-col h-[calc(100vh-96px-var(--regulatory-h))] lg:h-[calc(100vh-76px-var(--regulatory-h))]">
      {/* Header */}
      <div className="flex-shrink-0 mb-3 sm:mb-4">
        <PageHeader
          icon={Zap}
          title="Recherche Ciblée"
          subtitle="Réponses rapides et sourcées à vos questions pratiques"
          gradient="from-emerald-600 to-teal-600"
          testId="medical-search-header"
          actions={(
            <button
              type="button"
              data-testid="mobile-history-toggle"
              onClick={() => setShowHistory((v) => !v)}
              className={`lg:hidden flex items-center gap-1.5 h-10 px-3 rounded-xl border text-xs font-medium transition-colors ${showHistory ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-slate-900/60 border-slate-800 text-slate-300'}`}
              aria-label="Historique récent"
            >
              <History className="w-4 h-4" />
              <span>{searchHistory.length}</span>
            </button>
          )}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 grid-rows-[minmax(0,1fr)] gap-4 lg:gap-6 flex-1 min-h-0">
        {/* Main Search Area */}
        <div className="lg:col-span-2 flex flex-col min-h-0">
          {/* Scrollable content: résultat ou exemples */}
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 sm:space-y-6 pr-0.5 sm:pr-1">
            {showHistory ? (
              <div data-testid="mobile-history-panel" className="glassmorphic rounded-2xl p-3.5 sm:p-6 lg:hidden">
                <h3 className="text-base font-semibold text-white mb-3">Historique récent</h3>
                {historyList}
              </div>
            ) : result ? (
              <div data-testid="search-result" className="glassmorphic rounded-2xl p-3 sm:p-6">
                <h2 className="text-base sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
                  Résultat
                </h2>
                <div className="space-y-2.5 sm:space-y-4">
                  {sections.map((section, index) => (
                    <ResultCard key={`${section.title || 'text'}-${index}`} title={section.title} accent={section.accent}>
                      <RichText text={section.content} accent={section.accent} />
                    </ResultCard>
                  ))}
                </div>
              </div>
            ) : (
              /* Examples (visibles tant qu'aucun résultat) */
              <div className="glassmorphic rounded-2xl p-3.5 sm:p-6">
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <Lightbulb className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-base sm:text-lg font-semibold text-white">Exemples de questions</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                  {exampleQuestions.map((example, index) => (
                    <button
                      key={example}
                      data-testid={`example-${index}`}
                      onClick={() => loadExample(example)}
                      className="text-left p-3 min-h-[48px] rounded-lg bg-slate-900/40 border border-slate-800 hover:bg-slate-900/60 hover:border-emerald-500/30 active:scale-[0.99] transition-all text-sm text-slate-300"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search Form (stays at the bottom) */}
          <div className="glassmorphic rounded-2xl p-2.5 sm:p-4 flex-shrink-0 mt-2.5 sm:mt-4">
            <form onSubmit={handleSearch} className="space-y-2 sm:space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  data-testid="medical-search-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Posez votre question médicale — Ex: Posologie amoxicilline angine adulte ?"
                  className="w-full pl-11 pr-4 py-3 min-h-[48px] bg-slate-950/50 border border-slate-800 rounded-xl text-slate-200 text-[15px] sm:text-base placeholder:text-slate-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  required
                />
              </div>
              <div className="flex flex-row gap-2 sm:gap-3">
                <VoiceInput 
                  onTranscription={handleVoiceTranscription}
                  placeholder="Parler"
                  disabled={loading}
                  className="flex-shrink-0"
                />
                <Button
                  data-testid="search-button"
                  type="submit"
                  disabled={loading}
                  className="flex-1 min-h-[48px] rounded-xl px-4 sm:px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium transition-all shadow-[0_4px_14px_0_rgba(16,185,129,0.39)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.5)] hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      <span className="hidden sm:inline">Recherche en cours...</span>
                      <span className="sm:hidden">Recherche…</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" />
                      Rechercher
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Search History Sidebar (desktop) */}
        <div className="hidden lg:block glassmorphic rounded-2xl p-6 overflow-y-auto min-h-0">
          <h3 className="text-xl font-semibold text-white mb-4">Historique Récent</h3>
          {historyList}
        </div>
      </div>
    </div>
  );
};
